import { Alert, Button, Form, Input, Space, Typography, notification } from "antd";
import { KeyRound, LockKeyhole, Mail, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";

import { LoginChallenge } from "../../backend/security";
import { formatRemaining, useLoginMfaChallenge } from "./useLoginMfaChallenge";

interface LoginMfaChallengeProps {
    challenge: LoginChallenge;
    /** Receives every server response: a finished sign-in or the next challenge. */
    onResult: (result: any) => Promise<void>;
    onCancel: () => void;
    /** See useLoginMfaChallenge */
    preferPasskey?: boolean;
}

export default function LoginMfaChallenge({ challenge, onResult, onCancel, preferPasskey }: LoginMfaChallengeProps) {
    const {
        active, methods, requiresChoice, currentMethod, selectedMethod, setSelectedMethod,
        code, setCode, password, setPassword, error, setError, loading, secondsLeft, showExpiry, passkeySupported,
        sendEmailCode, verifyTotp, verifyPasskey, verifyPassword, chooseAnotherWay, alternative, switchTo,
    } = useLoginMfaChallenge({
        challenge, onResult, onCancel, preferPasskey,
        onAbandon: (message, description) => notification.error({ message, description }),
    });

    const expiryHint = showExpiry
        ? <Typography.Text type="secondary">This sign-in request expires in {formatRemaining(secondsLeft!)}</Typography.Text>
        : null;
    const backLink = requiresChoice
        ? <Button type="link" block onClick={chooseAnotherWay}>Sign in another way</Button>
        : <Button type="link" block onClick={onCancel}>Use a different account</Button>;
    const errorAlert = error ? <Alert type="error" showIcon message={error} /> : null;
    const unsupportedHint = passkeySupported
        ? null
        : <Typography.Text type="secondary">This browser can't use passkeys.</Typography.Text>;

    const passkeyButton = (
        <Button
            type="primary"
            block
            size="large"
            icon={<KeyRound size={18} />}
            loading={loading}
            disabled={!passkeySupported}
            onClick={verifyPasskey}
        >
            Continue with a passkey
        </Button>
    );

    if (requiresChoice && !selectedMethod) {
        const passkey = methods.find(method => method.type === "passkey");
        const authenticator = methods.find(method => method.type === "totp");
        const passwordMethod = methods.find(method => method.type === "password");
        const emailMethod = methods.find(method => method.type === "email");
        return (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {errorAlert}
                {/* Selecting the passkey starts the browser prompt in the same click.
                    A confirmation screen in between would only repeat this label. */}
                {passkey && passkeyButton}
                {passkey && unsupportedHint}
                {authenticator && (
                    <Button block size="large" icon={<Smartphone size={18} />} disabled={loading} onClick={() => setSelectedMethod(authenticator)}>
                        Use an authenticator app
                    </Button>
                )}
                {passwordMethod && (
                    <Button block size="large" icon={<LockKeyhole size={18} />} disabled={loading} onClick={() => setSelectedMethod(passwordMethod)}>
                        Use your password
                    </Button>
                )}
                {emailMethod && (
                    <Button block size="large" icon={<Mail size={18} />} disabled={loading} onClick={sendEmailCode}>
                        Email me a code
                    </Button>
                )}
                {expiryHint}
                <Button type="link" block onClick={onCancel}>Use a different account</Button>
            </Space>
        );
    }

    if (currentMethod?.type === "passkey") {
        return (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {errorAlert}
                {passkeyButton}
                {unsupportedHint}
                {expiryHint}
                {alternative && <Button type="link" block onClick={() => switchTo(alternative)}>Use your password instead</Button>}
                {backLink}
            </Space>
        );
    }

    if (currentMethod?.type === "password") {
        return (
            <Form layout="vertical" onFinish={verifyPassword}>
                <Form.Item label="Password" required validateStatus={error ? "error" : undefined} help={error}>
                    <Input.Password
                        autoFocus
                        autoComplete="current-password"
                        value={password}
                        onChange={event => { setPassword(event.target.value); setError(undefined); }}
                        prefix={<LockKeyhole size={18} />}
                        size="large"
                    />
                </Form.Item>
                <div className="login-forgot">
                    <Link to={`/auth/reset-password${active.username ? `?email=${encodeURIComponent(active.username)}` : ""}`}>Forgot password?</Link>
                </div>
                <Button htmlType="submit" type="primary" block size="large" loading={loading} disabled={!password}>Sign in</Button>
                {alternative && (
                    <Button block size="large" icon={<KeyRound size={18} />} disabled={loading} onClick={() => switchTo(alternative)} style={{ marginTop: 12 }}>
                        Sign in with a passkey
                    </Button>
                )}
                {expiryHint}
                {backLink}
            </Form>
        );
    }

    if (currentMethod?.type === "totp" || currentMethod?.type === "email") {
        const fromEmail = currentMethod.type === "email";
        const codeLabel = fromEmail
            ? "Six-digit code we emailed you"
            : "Six-digit code from your authenticator app";
        return (
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Form onFinish={verifyTotp} style={{ width: "100%" }} layout="vertical">
                    <Form.Item
                        label={codeLabel}
                        validateStatus={error ? "error" : undefined}
                        help={error}
                    >
                        <Input.OTP
                            length={6}
                            value={code}
                            onChange={value => { setCode(value); setError(undefined); }}
                            aria-label={codeLabel}
                            autoFocus
                        />
                    </Form.Item>
                    <Button htmlType="submit" type="primary" block size="large" loading={loading} disabled={code.length !== 6}>
                        Sign in
                    </Button>
                </Form>
                {expiryHint}
                {backLink}
            </Space>
        );
    }

    return (
        <Alert
            type="error"
            showIcon
            message="No supported sign-in method is available"
            description="Go back and use Google, GitHub, or another account."
            action={<Button onClick={onCancel}>Back</Button>}
        />
    );
}
